import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Eye,
  Glasses,
  Sun,
  Sparkles,
  MapPin,
  Phone,
  ShieldCheck,
  ChevronRight,
  Clock,
  Star
} from "lucide-react";
import InstallPWA from "../components/InstallPWA";

export default function LandingPage() {
  const navigate = useNavigate();

  // Animation variants for smooth, staggered load
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 80 } }
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 font-sans selection:bg-blue-500/30 overflow-hidden relative">
      
      {/* PREMIUM BACKGROUND GLOWS */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[50%] bg-blue-600/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[50%] bg-emerald-600/10 blur-[120px] rounded-full pointer-events-none" />

      {/* TOP NAVIGATION BAR */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-5 lg:px-16 bg-[#020617]/80 backdrop-blur-xl border-b border-white/5">
        <div className="flex items-center gap-3 text-white">
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-2.5 rounded-2xl shadow-lg shadow-blue-500/20">
            <Eye size={24} className="text-white" />
          </div>
          <div className="flex flex-col">
            <span className="font-black text-xl tracking-tight leading-none uppercase">Shruti</span>
            <span className="font-medium text-[10px] text-blue-400 tracking-widest uppercase">Eye Care & Opticals</span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <InstallPWA />
          {/* Subtle Admin Login Button */}
          <button
            onClick={() => navigate("/login")}
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-full font-bold text-xs text-slate-400 hover:text-white transition-all shadow-sm"
          >
            <ShieldCheck size={14} /> <span className="hidden sm:inline">Admin Login</span>
          </button>
        </div>
      </nav>

      {/* MAIN CUSTOMER HERO SECTION */}
      <div className="pt-48 pb-24 px-6 lg:px-12 max-w-7xl mx-auto flex flex-col items-center text-center relative z-10">
        
        <motion.div 
          initial="hidden" 
          animate="show" 
          variants={containerVariants}
          className="max-w-4xl flex flex-col items-center"
        >
          <motion.div variants={itemVariants} className="px-5 py-2 rounded-full bg-white/5 border border-white/10 text-slate-300 text-xs font-bold uppercase tracking-widest mb-6 flex items-center gap-2">
            <Sparkles size={14} className="text-amber-400" /> Premium Eyewear Collection
          </motion.div>

          {/* 5.0 RATING BADGE */}
          <motion.div variants={itemVariants} className="flex items-center gap-1.5 mb-6 bg-amber-500/10 border border-amber-500/20 px-4 py-1.5 rounded-full">
            <Star size={14} className="text-amber-400 fill-amber-400" />
            <span className="text-amber-400 font-bold text-sm tracking-wide">5.0 Highly Rated Opticals in Patna</span>
          </motion.div>

          <motion.h1 variants={itemVariants} className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-[1.1] mb-8 text-white">
            Vision Perfected. <br />
            <span className="bg-gradient-to-r from-blue-400 via-indigo-400 to-emerald-400 bg-clip-text text-transparent">
              Style Elevated.
            </span>
          </motion.h1>

          <motion.div
            variants={itemVariants}
            className="mb-10 flex justify-center"
          >
            <img
              src="/shrutieyecareandopticals.png"
              alt="Shruti Eye Care & Opticals"
              className="w-40 md:w-52 lg:w-60 object-contain drop-shadow-[0_0_30px_rgba(59,130,246,0.35)]"
            />
          </motion.div>

          <motion.p variants={itemVariants} className="text-lg md:text-xl text-slate-400 max-w-2xl leading-relaxed mb-12">
            Experience world-class computerized eye testing and explore our curated collection of luxury frames, lenses, and sunglasses designed to fit your lifestyle.
          </motion.p>

          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-5 w-full sm:w-auto justify-center">
            <a 
              href="#services"
              className="px-10 py-4 bg-white text-slate-950 rounded-full font-black text-lg flex items-center justify-center gap-2 transition-transform hover:scale-105 shadow-xl shadow-white/10"
            >
              Explore Services <ChevronRight size={20} />
            </a>
            <a 
              href="#contact"
              className="px-10 py-4 bg-slate-900 border border-slate-800 hover:bg-slate-800 text-white rounded-full font-bold text-lg flex items-center justify-center gap-2 transition-colors"
            >
              <MapPin size={20} className="text-blue-400" /> Visit Store
            </a>
          </motion.div>
        </motion.div>

        {/* CUSTOMER SERVICES / PRODUCTS GRID */}
        <motion.div 
          id="services"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full mt-32 z-20 relative text-left"
        >
          <ServiceCard 
            icon={<Eye />} 
            title="Clinical Eye Testing" 
            desc="State-of-the-art computerized eye exams by certified optical professionals."
            color="text-blue-400" bg="bg-blue-500/10" border="border-blue-500/20"
          />
          <ServiceCard 
            icon={<Glasses />} 
            title="Premium Frames" 
            desc="Discover top global brands and lightweight, stylish frames for everyday wear."
            color="text-emerald-400" bg="bg-emerald-500/10" border="border-emerald-500/20"
          />
          <ServiceCard 
            icon={<Sun />} 
            title="Luxury Sunglasses" 
            desc="100% UV-protected designer sunglasses to keep your eyes safe in style."
            color="text-amber-400" bg="bg-amber-500/10" border="border-amber-500/20"
          />
          <ServiceCard 
            icon={<Sparkles />} 
            title="Contact Lenses" 
            desc="Comfortable daily, monthly, and colored lenses tailored to your prescription."
            color="text-purple-400" bg="bg-purple-500/10" border="border-purple-500/20"
          />
        </motion.div>

        {/* STORE INFO SECTION WITH REAL DATA */}
        <motion.div 
          id="contact"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="w-full mt-32 bg-slate-900/50 border border-slate-800 rounded-3xl p-10 lg:p-16 flex flex-col md:flex-row gap-10 items-center justify-between text-left"
        >
          <div className="space-y-6 flex-1">
            <h2 className="text-3xl font-black text-white">Visit Our Optical Studio</h2>
            <p className="text-slate-400 text-lg max-w-md">
              Drop by to check your vision, repair your frames, or find your next perfect pair of glasses in Patna.
            </p>
            
            <div className="space-y-5 pt-4">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 text-blue-400"><MapPin size={20} /></div>
                <div>
                  <h4 className="font-bold text-white text-base mb-1">Store Location</h4>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Shruti Eye Care & Opticals<br />
                    27, Akashwani Rd, Jyotipuram Colony, Khajpura <br />
                    Patna, Bihar 800014
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 text-emerald-400"><Clock size={20} /></div>
                <div>
                  <h4 className="font-bold text-white text-base mb-1">Operating Hours</h4>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Monday to Sunday<br />
                    Opens 10:00 AM
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 text-amber-400"><Phone size={20} /></div>
                <div>
                  <h4 className="font-bold text-white text-base mb-1">Contact Us</h4>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    0612 359 0450
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Map Visual Placeholder */}
          <div className="w-full md:w-1/2 h-80 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
          <iframe
            title="Shruti Eye Care & Opticals"
            src="https://www.google.com/maps?q=Shruti+Eye+Care+%26+Opticals,+Bihar&output=embed"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          
          <a
            href="https://www.google.com/maps/search/?api=1&query=Shruti+Eye+Care+%26+Opticals+Muzaffarpur+Bihar"
            target="_blank"
            rel="noreferrer"
            className="absolute bottom-4 right-4 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold uppercase tracking-wider rounded-full shadow-lg transition-colors z-10"
          >
            Open in Maps
          </a>
        </div>
        </motion.div>

        {/* CUSTOMER FOOTER */}
        <footer className="mt-24 pt-8 border-t border-slate-800/50 w-full flex flex-col md:flex-row justify-between items-center text-slate-500 text-sm gap-4">
          <div className="flex items-center gap-2">
            <Eye size={16} className="text-slate-400" />
            <span className="font-bold text-slate-300">Shruti Eye Care & Opticals</span>
          </div>
          <span>© {new Date().getFullYear()} All Rights Reserved.</span>
         
        </footer>

      </div>
    </div>
  );
}

/* PREMIUM SERVICE CARD COMPONENT */
function ServiceCard({ icon, title, desc, color, bg, border }) {
  return (
    <motion.div
      whileHover={{ y: -8 }}
      className="bg-slate-900/40 backdrop-blur-md border border-slate-800 rounded-3xl p-8 hover:bg-slate-800/80 transition-all duration-300"
    >
      <div className={`w-14 h-14 rounded-2xl ${bg} ${border} ${color} flex items-center justify-center mb-6 shadow-inner`}>
        {icon}
      </div>
      <h3 className="font-black text-xl text-white tracking-wide mb-3">{title}</h3>
      <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
    </motion.div>
  );
}